import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

/**
 * Pill face after the reference design: 40 px, 10 px radius, display face.
 * Active pills are filled with the primary; the rest carry a hairline.
 */
const pillBaseClassName =
  'dc-pill inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-control px-4 font-display text-sm font-semibold transition-[background-color,color,filter] duration-[120ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:pointer-events-none disabled:opacity-40';
const pillActiveClassName = 'bg-primary text-primary-fg';
const pillInactiveClassName =
  'bg-transparent text-ink ring-1 ring-inset ring-line hover:bg-paper-2';

// ═══════════════════════════════════════════════════════════════════
// PillGroup / Pill — plain buttons (filters, navigation)
// ═══════════════════════════════════════════════════════════════════

/**
 * How an active pill announces itself:
 * - `toggle`  filters and options, `aria-pressed`
 * - `nav`     the current page or step, `aria-current="page"`
 */
type PillMode = 'toggle' | 'nav';

const PillModeContext = React.createContext<PillMode>('toggle');

export interface PillGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  mode?: PillMode;
}

const PillGroup = React.forwardRef<HTMLDivElement, PillGroupProps>(
  ({ mode = 'toggle', className, ...props }, ref) => (
    <PillModeContext.Provider value={mode}>
      <div
        ref={ref}
        role={mode === 'nav' ? undefined : 'group'}
        className={cn('flex flex-wrap gap-2', className)}
        {...props}
      />
    </PillModeContext.Provider>
  )
);
PillGroup.displayName = 'PillGroup';

export interface PillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  /** Render the child element (e.g. a router `Link`) instead of a button. */
  asChild?: boolean;
}

const Pill = React.forwardRef<HTMLButtonElement, PillProps>(
  ({ active = false, asChild = false, className, type, ...props }, ref) => {
    const mode = React.useContext(PillModeContext);
    const Comp = asChild ? Slot : 'button';
    const stateProps =
      mode === 'nav'
        ? { 'aria-current': active ? ('page' as const) : undefined }
        : { 'aria-pressed': active };
    return (
      <Comp
        ref={ref}
        type={asChild ? type : (type ?? 'button')}
        className={cn(
          pillBaseClassName,
          active ? pillActiveClassName : pillInactiveClassName,
          className
        )}
        {...stateProps}
        {...props}
      />
    );
  }
);
Pill.displayName = 'Pill';

// ═══════════════════════════════════════════════════════════════════
// Tabs — Radix tabs in the same pill face
// ═══════════════════════════════════════════════════════════════════

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('flex flex-wrap gap-2', className)}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      pillBaseClassName,
      'data-[state=active]:bg-primary data-[state=active]:text-primary-fg',
      'data-[state=inactive]:bg-transparent data-[state=inactive]:text-ink data-[state=inactive]:ring-1 data-[state=inactive]:ring-inset data-[state=inactive]:ring-line data-[state=inactive]:hover:bg-paper-2',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { PillGroup, Pill, Tabs, TabsList, TabsTrigger, TabsContent };
export type { PillMode };
